import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Message } from "../../../../../../../lib/chat-store/types";
import ChatBubble from "./chat-bubble";
import styles from "./message-list.module.css";
import { useChatStore } from "../../../../../../../lib/chat-store/chats-store";
import { IoArrowDown } from "react-icons/io5";
import ChatLoadingSkeleton from "./bubbles/chat-loading-skeleton";
import { useTranslations } from "next-intl";

export default function MessageList({
  messages,
  isAssistantTyping,
  test_image_url,
}: {
  messages: Message[];
  isAssistantTyping?: boolean;
  test_image_url: string;
}) {
  const t = useTranslations("ChatsView");
  const containerRef = useRef<HTMLDivElement>(null);
  const openChatId = useChatStore((state) => state.openChatId);
  const chats = useChatStore((state) => state.chatsState.chats);
  const getChatMessages = useChatStore((state) => state.getChatMessages);
  const activeChat = chats.find((c) => c.conversation_id === openChatId);
  const hasMore = activeChat?.has_more_pages ?? false;
  const cursor = activeChat?.last_cursor ?? null;
  const isInitialLoad = activeChat?.is_loading_messages;

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const prevMessagesLengthRef = useRef(messages.length);
  const scrollHeightRef = useRef(0);
  const blockScrollRef = useRef(false);
  const lastMessageIdRef = useRef<string>(null);
  const currentChatIdRef = useRef(openChatId);

  const handleScroll = () => {
    if (!containerRef.current || blockScrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    const isAtBottom = Math.abs(scrollTop) < 50;

    if (isAtBottom) {
      setShowScrollButton(false);
    }

    if (
      Math.abs(scrollTop) + clientHeight >= scrollHeight - 50 &&
      hasMore &&
      !isLoadingMore &&
      activeChat
    ) {
      setIsLoadingMore(true);
      scrollHeightRef.current = scrollHeight;
      getChatMessages(activeChat.conversation_id, cursor);
    }
  };

  useLayoutEffect(() => {
    const prevLength = prevMessagesLengthRef.current;
    const currentLength = messages.length;
    const newMessagesAdded = currentLength > prevLength;
    prevMessagesLengthRef.current = currentLength;

    if (isLoadingMore && newMessagesAdded) {
      setIsLoadingMore(false);
    }
  }, [messages, isLoadingMore]);

  useEffect(() => {
    if (currentChatIdRef.current !== openChatId) {
      currentChatIdRef.current = openChatId;
      lastMessageIdRef.current = messages[messages.length - 1]?.message_id;
      return;
    }

    const latestMessageId = messages[messages.length - 1]?.message_id;

    if (latestMessageId && latestMessageId !== lastMessageIdRef.current) {
      if (lastMessageIdRef.current !== undefined && containerRef.current) {
        const isAtBottom = Math.abs(containerRef.current.scrollTop) < 100;

        if (!isAtBottom) {
          setShowScrollButton(true);
        }
      }
      lastMessageIdRef.current = latestMessageId;
    }
  }, [messages, openChatId]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      setShowScrollButton(false);
    }
  };

  useEffect(() => {
    blockScrollRef.current = true;
    if (containerRef.current) containerRef.current.scrollTop = 0;

    setIsLoadingMore(false);
    setShowScrollButton(false);

    const timer = setTimeout(() => {
      blockScrollRef.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, [openChatId]);

  if (isInitialLoad && messages.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.messageList}>
          <ChatLoadingSkeleton />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return <div className={styles.emptyState}>{t("noMessagesYet")}</div>;
  }

  // REVERSE the messages because column-reverse starts from the bottom
  const displayMessages = [...messages].reverse();

  return (
    <div className={styles.wrapper}>
      <div
        ref={containerRef}
        className={styles.messageList}
        onScroll={handleScroll}
      >
        {/* Typing indicator stays at the very bottom (first in DOM for column-reverse) */}
        {isAssistantTyping && (
          <div className={`${styles.row} ${styles.theirs}`}>
            <div className={styles.typingBubble}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
            </div>
          </div>
        )}

        {displayMessages.map((message) => (
          <ChatBubble
            key={message.message_id}
            message={message}
            test_image_url={test_image_url}
            chatData={activeChat}
          />
        ))}

        {isLoadingMore && (
          <div className={styles.loadingHistory}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>

      {showScrollButton && (
        <button className={styles.scrollButton} onClick={scrollToBottom}>
          <IoArrowDown />
          <span>{t("newMessages")}</span>
        </button>
      )}
    </div>
  );
}
